import { resolve } from '@scalar/workspace-store/resolve'
import type { SchemaObject } from '@scalar/workspace-store/schemas/v3.2/strict/openapi-document'

import { compositions } from './schema-composition'

/** Display normalization tracks null branches without changing the stored OpenAPI schema. */
type DisplaySchema = SchemaObject & { nullable?: boolean }

/**
 * Shallow-merges schema-like objects, but unions `properties` and `required`
 * instead of letting a later object's `properties`/`required` completely
 * overwrite an earlier one's. Without this, a variant schema (e.g. a `oneOf`
 * branch) that declares its own `properties`/`required` would wipe out the
 * shared base fields factored out at the root (or via a sibling `allOf`)
 * instead of being combined with them (#9657).
 */
function mergeSchemaProperties(...objects: (Record<string, unknown> | undefined)[]): Record<string, unknown> {
  const merged: Record<string, unknown> = {}
  const properties: Record<string, unknown> = {}
  const required = new Set<string>()
  let hasProperties = false
  let hasRequired = false

  for (const object of objects) {
    if (!object) {
      continue
    }

    for (const [key, val] of Object.entries(object)) {
      if (key === 'properties' && val && typeof val === 'object') {
        Object.assign(properties, val)
        hasProperties = true
        continue
      }

      if (key === 'required' && Array.isArray(val)) {
        for (const name of val) {
          required.add(name)
        }
        hasRequired = true
        continue
      }

      merged[key] = val
    }
  }

  if (hasProperties) {
    merged.properties = properties
  }
  if (hasRequired) {
    merged.required = [...required]
  }

  return merged
}

/**
 * Normalize compositions for display without changing the source schema. Null branches
 * become nullable state, single branches are flattened, and shared properties and
 * required fields are merged into variants so the renderer keeps their full context.
 */
export function optimizeValueForDisplay(value: DisplaySchema | undefined): DisplaySchema | undefined {
  if (!value || typeof value !== 'object') {
    return value
  }

  // Find the composition keyword early to avoid unnecessary work
  const composition = compositions.find((keyword) => keyword in value && keyword !== 'not')

  // If there's no relevant composition keyword, return a shallow copy
  if (!composition) {
    return { ...value }
  }

  const schemas = value[composition]
  if (!Array.isArray(schemas)) {
    return { ...value }
  }

  // Extract root properties efficiently (excluding composition and nullable)
  const { [composition]: _, nullable: originalNullable, ...rootProperties } = value
  const hasRootProperties = Object.keys(rootProperties).length > 0

  // Check for null schemas and filter them out in one pass
  const { filteredSchemas, hasNullSchema } = schemas.reduce<{
    filteredSchemas: SchemaObject[]
    hasNullSchema: boolean
  }>(
    (acc, _schema) => {
      const schema = resolve.schema(_schema)

      if ('type' in schema && schema.type === 'null') {
        acc.hasNullSchema = true
      } else {
        acc.filteredSchemas.push(schema)
      }
      return acc
    },
    { filteredSchemas: [], hasNullSchema: false },
  )

  // Determine if nullable should be set
  const shouldBeNullable = hasNullSchema || originalNullable === true

  // If only one schema remains after filtering, merge with root properties.
  // `properties`/`required` are unioned so sibling properties declared next to
  // the composition (e.g. `properties` alongside a single-`$ref` `allOf`) are
  // kept instead of being overwritten by the member schema's own `properties`.
  // Root-level annotations (title, description, …) win over the member's: they
  // describe the combined schema, not the base it extends.
  if (filteredSchemas.length === 1) {
    const mergedSchema = mergeSchemaProperties(filteredSchemas[0], rootProperties) as DisplaySchema
    if (shouldBeNullable) {
      mergedSchema.nullable = true
    }
    return mergedSchema
  }

  // Check if root properties should be merged into schemas
  const shouldMergeRootProperties =
    (composition === 'oneOf' || composition === 'anyOf') &&
    (hasRootProperties || filteredSchemas.some((schema) => schema.allOf))

  if (shouldMergeRootProperties) {
    const mergedSchemas = filteredSchemas.map((_schema) => {
      const schema = resolve.schema(_schema)

      // Flatten single-item allOf and merge with root properties. If the
      // variant has its own properties/required/$ref, the allOf member's own
      // identity (`$ref`, `title`, `name`) is dropped before merging so it
      // cannot overwrite the variant's, which would otherwise mislabel the
      // flattened schema as the base it extends instead of the variant itself
      // (#9964). `getModelNameFromSchema` labels a variant by title → name →
      // $ref, so any of those leaking from the base is enough to mislabel it.
      // A variant that is a bare wrapper around the allOf member has no
      // identity of its own to protect, so the base's identity is kept as a
      // fallback label.
      if (schema.allOf?.length === 1) {
        const { allOf, ...otherProps } = schema
        const allOfMember = resolve.schema(allOf[0])!
        const variantHasOwnIdentity = 'properties' in otherProps || 'required' in otherProps || '$ref' in otherProps
        if (variantHasOwnIdentity) {
          const { $ref: _ref, title: _title, name: _name, ...allOfMemberWithoutIdentity } = allOfMember
          return mergeSchemaProperties(rootProperties, otherProps, allOfMemberWithoutIdentity)
        }
        return mergeSchemaProperties(rootProperties, otherProps, allOfMember)
      }
      return mergeSchemaProperties(rootProperties, schema)
    })

    // @ts-expect-error - We avoid using coerceValue here as it may be dangerous, so we type cast
    const result = { [composition]: mergedSchemas } as DisplaySchema
    if (typeof value.description === 'string') {
      result.description = value.description
    }
    if (shouldBeNullable) {
      result.nullable = true
    }
    return result
  }

  // Return with filtered schemas if any nulls were removed
  if (filteredSchemas.length !== schemas.length) {
    const result: DisplaySchema = { ...value, [composition]: filteredSchemas }
    if (shouldBeNullable) {
      result.nullable = true
    }
    return result
  }

  return { ...value }
}
